/**
 * WebSocketProvider — React adapter over WebSocketClient.
 *
 * Creates the client on mount, subscribes to status changes,
 * and exposes the client API through React context.
 *
 * The WebSocketClient instance is scoped to the selected workspace.
 * Reactive UI values (backendReady, status) live in state.
 * The context value is memoized for a stable consumer API.
 */

import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { JSX } from "react";
import { WebSocketClient, type Rpc, type StreamEvent } from "../store/websocketClient";
import { getAuthToken, useAuthStore } from "../store/authStore";
import { getCurrentWorkspace, useWorkspaceStore } from "../store/workspaceStore";

// ─── Context ─────────────────────────────────────────────────────────

export interface WebSocketContextValue {
  readonly rpc: Rpc;
  readonly backendReady: boolean;
  readonly status: string;
  readonly onSchemaEvent: (schemaIds: readonly string[], handler: (e: StreamEvent) => void) => () => void;
  readonly onEventType: (types: readonly string[], handler: (e: StreamEvent) => void) => () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// Re-export types so existing consumer imports from this file keep working
export type { Rpc, StreamEvent };

function noopUnsubscribe(): void {
  void 0;
}

// ─── Provider ────────────────────────────────────────────────────────

export function WebSocketProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const workspaceReady = useWorkspaceStore((state) => state.ready);
  useWorkspaceStore((state) => state.selectedWorkspaceId);
  const currentWorkspace = getCurrentWorkspace();
  const client = useMemo(() => {
    if (!currentWorkspace?.apiBaseUrl) return null;
    return new WebSocketClient(
      currentWorkspace.apiBaseUrl,
      (): string | null => getAuthToken(),
      // Stale token rejected by the server → drop it so the reconnect goes
      // token-less (Open mode admits the local default user). Fixes the
      // permanent "Connecting…" wedge on an invalid stored token.
      (): void => { useAuthStore.getState().clearStaleToken(); },
    );
  }, [currentWorkspace]);

  // Reactive UI state
  const [backendReady, setBackendReady] = useState(false);
  const [status, setStatus] = useState("Initializing...");

  useEffect(() => {
    // @tested-by: tst_fe_ws_provider_001, tst_fe_ws_provider_002,
    //             tst_fe_ws_provider_003, tst_fe_ws_provider_004
    if (!workspaceReady) {
      setBackendReady(false);
      setStatus("Loading workspace...");
      return;
    }
    if (client === null) {
      setBackendReady(false);
      setStatus("No workspace selected");
      return;
    }

    setBackendReady(client.getStatus().connected);
    setStatus(client.getStatus().label);
    const unsub = client.subscribeStatus((s) => {
      setBackendReady(s.connected);
      setStatus(s.label);
    });

    // Always connect — do NOT gate on a stored token. The auth handshake admits
    // the local single-user default (Open mode) WITHOUT a token, so a fresh local
    // app connects on first launch. Gating connect() on a token meant a
    // token-less local app never even opened the socket → permanent "Connecting…".
    // (In Required mode the server still rejects the token-less frame → the UI
    // shows "Not signed in" and the login flow can mint a token.)
    client.connect();

    const unsubAuth = useAuthStore.subscribe((state, prev) => {
      if (state.token === prev.token) return;
      if (state.token !== null) {
        // Token replacement is also a session boundary: connect() cleans up
        // the old socket before authenticating with the current token.
        client.connect();
      } else {
        client.disconnect();
      }
    });

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      unsub();
      unsubAuth();
      client.disconnect();
    };
  }, [client, workspaceReady]);

  // Stable API surface. Keep generic typing on separately typed functions to
  // avoid TSX parser ambiguity for inline `<T = ...>` arrow syntax.
  const rpc = useMemo<Rpc>(() => {
    const rpcCall: Rpc["rpc"] = (method, params) => {
      if (client === null) {
        return Promise.reject(new Error("WebSocket client unavailable"));
      }
      return client.rpc(method, params);
    };
    const rpcStreamCall: Rpc["rpcStream"] = (method, params, onChunk) => {
      if (client === null) {
        return Promise.reject(new Error("WebSocket client unavailable"));
      }
      return client.rpcStream(method, params, onChunk);
    };
    return {
      rpc: rpcCall,
      rpcStream: rpcStreamCall,
      baseUrl: client?.baseUrl ?? currentWorkspace?.apiBaseUrl ?? "",
    };
  }, [client, currentWorkspace]);

  const onSchemaEvent = useMemo(
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    () => (ids: readonly string[], h: (e: StreamEvent) => void) => client?.onSchemaEvent(ids, h) ?? noopUnsubscribe,
    [client],
  );

  const onEventType = useMemo(
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    () => (types: readonly string[], h: (e: StreamEvent) => void) => client?.onEventType(types, h) ?? noopUnsubscribe,
    [client],
  );

  const value = useMemo<WebSocketContextValue>(
    () => ({ rpc, backendReady, status, onSchemaEvent, onEventType }),
    [rpc, backendReady, status, onSchemaEvent, onEventType],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
